import { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Button,
  StatusBadge,
  NotificationCard,
  Modal,
  ModalBody,
  ModalFooter,
  useToast,
} from '@sighthost/ui';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Play,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { supabase } from '@/shared/lib/supabase';

/**
 * DT-1 admin Execute panel (DOM-T6, 2026-05-11).
 *
 * Rendered in the sidebar of TicketDetailPage when ticket.department === 'domains'.
 * Reads the ticket's workflow_state + linked invoice + reseller balance,
 * and exposes one Execute button that calls `staff_execute_domain_intent()`.
 *
 * Spec: _bmad-output/PHASE_DT1_ARCHITECTURE_2026-05-11.md §1 / DOM-T6
 */

type WorkflowState =
  | 'open'
  | 'awaiting_payment'
  | 'pending_staff_action'
  | 'executing'
  | 'execution_succeeded'
  | 'execution_failed'
  | 'cancelled_by_customer'
  | 'cancelled_by_staff'
  | 'resolved';

interface TicketRow {
  id: string;
  workflow_state: {
    state: WorkflowState;
    failure_reason?: string;
    transitioned_at?: string;
  } | null;
  metadata: {
    operation?: 'register' | 'renew' | 'transfer_in';
    domain_name?: string;
    period_years?: number;
  } | null;
  status: string;
}

interface InvoiceRow {
  id: string;
  number: string;
  total: number;
  lifecycle_state: string;
}

interface BalanceResponse {
  balance: number;
  floor: number;
  ok: boolean;
}

interface Props {
  ticketId: string;
  onActionComplete?: () => void;
}

export function DomainExecutePanel({ ticketId, onActionComplete }: Props) {
  const { toast } = useToast();
  const [ticket, setTicket] = useState<TicketRow | null>(null);
  const [invoice, setInvoice] = useState<InvoiceRow | null>(null);
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  // Execute spends prepaid reseller balance at domains.co.za and, for register
  // and transfer_in, is not reversible once the registry accepts it. It was a
  // single unguarded click sitting next to ordinary ticket controls. Confirm
  // first, with the operation, the domain and the money spelled out.
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchState = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, iRes, bRes] = await Promise.all([
        supabase
          .from('ticket')
          .select('id, workflow_state, metadata, status')
          .eq('id', ticketId)
          .maybeSingle(),
        supabase
          .from('invoice')
          .select('id, number, total, lifecycle_state')
          .eq('intent_ticket_id', ticketId)
          .maybeSingle(),
        supabase.functions.invoke<BalanceResponse>('domain-balance', { method: 'GET' }),
      ]);
      if (tRes.data) setTicket(tRes.data as unknown as TicketRow);
      setInvoice((iRes.data as unknown as InvoiceRow | null) ?? null);
      setBalance(bRes.data ?? null);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  async function execute() {
    if (!ticket) return;
    setExecuting(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const staffId = userRes.user?.id;
      if (!staffId) {
        toast({ tone: 'danger', title: 'Not authenticated', body: '' });
        return;
      }
      const { data, error } = await supabase.rpc('staff_execute_domain_intent', {
        p_ticket_id: ticketId,
        p_staff_id: staffId,
      });
      if (error) {
        const code = (error as { message?: string }).message ?? 'execute_failed';
        toast({
          tone: 'danger',
          title: 'Execute failed',
          body: friendlyExecuteError(code),
        });
        return;
      }
      toast({
        tone: 'success',
        title: 'Job queued',
        body: `${ticket.metadata?.operation ?? 'Operation'} for ${ticket.metadata?.domain_name ?? ''} is running.`,
      });
      // Refresh self + notify parent
      await fetchState();
      onActionComplete?.();
    } catch (e) {
      toast({
        tone: 'danger',
        title: 'Execute failed',
        body: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setExecuting(false);
    }
  }

  if (loading && !ticket) {
    return (
      <Card interactive={false}>
        <header className="border-b border-border px-4 py-2">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Domain action
          </h3>
        </header>
        <div className="px-4 py-3 text-xs text-muted-foreground">Loading…</div>
      </Card>
    );
  }

  if (!ticket) return null;

  const state: WorkflowState =
    (ticket.workflow_state?.state as WorkflowState | undefined) ?? 'open';
  const operation = ticket.metadata?.operation;
  const domain = ticket.metadata?.domain_name;
  const balanceTooLow = balance && balance.balance < balance.floor;
  const invoicePaid = invoice?.lifecycle_state === 'paid';
  // transfer_in is payment-gated too: domain-transfer-confirm raises a
  // domain_registration invoice priced like a registration (R99 for .co.za).
  const needsPayment =
    operation === 'register' || operation === 'renew' || operation === 'transfer_in';
  const awaitingPayment = needsPayment && !invoicePaid;

  // Determine button state.
  // transfer_in was excluded here while staff_execute_domain_intent still raised
  // 'transfer_in_pending_v1_5'. Both were implemented 2026-07-30 — the EF
  // (domain-transfer-confirm) and the runner handler (runDomainTransfer) had
  // been complete all along, so only this middle step was missing. That is why
  // a transfer looked like something an agent had "forgotten to send".
  const canExecute =
    state === 'pending_staff_action' && !awaitingPayment && !balanceTooLow;

  const canRetry = state === 'execution_failed' && !balanceTooLow;

  return (
    <Card interactive={false}>
      <header className="border-b border-border px-4 py-2">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Domain action
        </h3>
      </header>
      <div className="space-y-3 px-4 py-3 text-sm">
        <div className="space-y-1">
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">Operation</span>
            <span className="font-medium text-foreground">{operation ?? '—'}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">Domain</span>
            <code className="text-xs tabular-nums">{domain ?? '—'}</code>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">State</span>
            <StateBadge state={state} />
          </div>
          {invoice && (
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">Invoice</span>
              <span className="text-xs tabular-nums">
                {invoice.number} · R{invoice.total.toFixed(2)} ·{' '}
                <StatusBadge tone={invoicePaid ? 'paid' : 'pending'}>
                  {invoice.lifecycle_state}
                </StatusBadge>
              </span>
            </div>
          )}
          {balance && (
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">Reseller balance</span>
              <span
                className={
                  'text-xs tabular-nums ' +
                  (balanceTooLow ? 'font-semibold text-danger' : 'text-foreground')
                }
              >
                R{balance.balance.toFixed(2)} / floor R{balance.floor.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {balanceTooLow && (
          <NotificationCard
            tone="danger"
            title="Balance below floor"
            body={
              <span className="text-xs">
                Top up the domains.co.za reseller account before executing. The
                registrar will refuse the mutation otherwise.
              </span>
            }
          />
        )}

        {awaitingPayment && (
          <div className="rounded border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning-dark">
            <AlertTriangle className="mr-1 inline h-3 w-3" />
            Awaiting customer payment on invoice {invoice?.number}.
          </div>
        )}

        {operation === 'transfer_in' && !awaitingPayment && (
          <div className="rounded border border-info/30 bg-info/5 px-3 py-2 text-xs text-info">
            <AlertTriangle className="mr-1 inline h-3 w-3" />
            ZACR transfers take <strong>5–7 days</strong> and no EPP key is needed for{' '}
            <strong>.za</strong>. Executing starts the transfer at the registrar and then polls
            until it completes — the domain keeps working at its current host throughout, and
            its nameservers move to ours only once the transfer lands.
          </div>
        )}

        {state === 'execution_succeeded' && (
          <div className="rounded border border-success/30 bg-success/5 px-3 py-2 text-xs text-success">
            <CheckCircle2 className="mr-1 inline h-3 w-3" />
            Operation completed successfully.
          </div>
        )}

        {state === 'execution_failed' && (
          <div className="rounded border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
            <XCircle className="mr-1 inline h-3 w-3" />
            {ticket.workflow_state?.failure_reason ?? 'Execution failed.'}
          </div>
        )}

        {(state === 'cancelled_by_customer' || state === 'cancelled_by_staff') && (
          <div className="rounded border border-border bg-card-subtle px-3 py-2 text-xs text-muted-foreground">
            Request cancelled.
          </div>
        )}

        {canExecute && (
          <Button
            onClick={() => setConfirmOpen(true)}
            loading={executing}
            disabled={executing}
            className="w-full"
          >
            <Play className="h-4 w-4" />
            Execute {operation}
          </Button>
        )}

        {canRetry && (
          <Button
            onClick={() => setConfirmOpen(true)}
            loading={executing}
            disabled={executing}
            variant="outline"
            className="w-full"
          >
            <RotateCcw className="h-4 w-4" />
            Retry
          </Button>
        )}

        <Modal
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={`Execute ${operation ?? 'domain action'}?`}
          description="This runs against the live registrar."
        >
          <ModalBody>
            <dl className="space-y-2 text-sm">
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-xs text-muted-foreground">Operation</dt>
                <dd className="font-medium text-foreground">{operation ?? '—'}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt className="text-xs text-muted-foreground">Domain</dt>
                <dd className="break-all font-mono text-xs">{domain ?? '—'}</dd>
              </div>
              {invoice && (
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-xs text-muted-foreground">Customer invoice</dt>
                  <dd className="tabular-nums">
                    {invoice.number} · R{invoice.total.toFixed(2)}
                  </dd>
                </div>
              )}
              {balance && (
                <div className="flex items-baseline justify-between gap-4">
                  <dt className="text-xs text-muted-foreground">Reseller balance</dt>
                  <dd className="tabular-nums">R{balance.balance.toFixed(2)}</dd>
                </div>
              )}
            </dl>
            <p className="mt-3 text-xs text-muted-foreground">
              {operation === 'renew'
                ? 'This spends prepaid reseller balance immediately. A renewal cannot be un-renewed.'
                : operation === 'transfer_in'
                  ? 'This starts a ZACR transfer that runs for 5-7 days and cannot be recalled once the registry accepts it.'
                  : 'This spends prepaid reseller balance immediately and registers the domain at the registry. It cannot be undone.'}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmOpen(false);
                void execute();
              }}
              loading={executing}
              disabled={executing}
            >
              Yes, execute {operation}
            </Button>
          </ModalFooter>
        </Modal>

        {state === 'executing' && (
          <Button disabled className="w-full">
            <Loader2 className="h-4 w-4 animate-spin" />
            Job in progress…
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={fetchState}
          disabled={loading}
          className="w-full text-xs"
        >
          Refresh
        </Button>
      </div>
    </Card>
  );
}

function StateBadge({ state }: { state: WorkflowState }) {
  const labelMap: Record<WorkflowState, { label: string; tone: 'pending' | 'info' | 'active' | 'paid' | 'failed' | 'draft' }> = {
    open: { label: 'Open', tone: 'pending' },
    awaiting_payment: { label: 'Awaiting payment', tone: 'info' },
    pending_staff_action: { label: 'Ready to execute', tone: 'pending' },
    executing: { label: 'Executing', tone: 'info' },
    execution_succeeded: { label: 'Done', tone: 'paid' },
    execution_failed: { label: 'Failed', tone: 'failed' },
    cancelled_by_customer: { label: 'Cancelled', tone: 'draft' },
    cancelled_by_staff: { label: 'Cancelled', tone: 'draft' },
    resolved: { label: 'Resolved', tone: 'paid' },
  };
  const { label, tone } = labelMap[state];
  return <StatusBadge tone={tone}>{label}</StatusBadge>;
}

function friendlyExecuteError(code: string): string {
  if (code.includes('forbidden_not_staff')) return 'You need staff permissions to execute.';
  if (code.includes('ticket_not_found')) return 'Ticket no longer exists.';
  if (code.includes('ticket_already_executing')) return 'A job is already in progress for this ticket.';
  if (code.includes('ticket_metadata_incomplete')) return 'Ticket is missing required metadata.';
  if (code.includes('order_not_found')) return 'Linked order not found — invoice may not be paid yet.';
  if (code.includes('domain_link_not_found')) return 'The domain link no longer exists.';
  // Kept: an old ticket could still surface this from a cached client bundle,
  // though the RPC no longer raises it (implemented 2026-07-30).
  if (code.includes('transfer_in_pending_v1_5')) {
    return 'Transfer-in is now supported — reload the page to pick up the new version.';
  }
  if (code.includes('order_step_mismatch')) {
    return 'This ticket is a transfer but the linked order is a registration (or vice versa). Do not execute — raise it with billing, the two spend money at different registrar endpoints.';
  }
  return code;
}
